import { Image, Text, View } from '@react-pdf/renderer'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { ReportPage } from 'features/reporting/pdfGeneration/components/shared/ReportPage'
import { PdfProps, StaticSection } from 'features/reporting/types'
import robotImage from 'pages/reporting/reports/rug/images/intro_to_rug_image_1.png'
import dataImage from 'pages/reporting/reports/rug/images/intro_to_rug_image_2.png'
import { RugReportSectionId } from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import React from 'react'

const IntroText: React.FC<{ children: React.ReactNode }> = (props) => {
  return (
    <Text style={{ fontSize: 10, lineHeight: 1.3, marginBottom: 14 }}>
      {props.children}
    </Text>
  )
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData } = useRugReportOps()

  return (
    <ReportPage
      id='intro-to-rug'
      wrap={false}
      tracker={{
        sectionId: RugReportSectionId.INTRO_TO_RUG,
        onRender,
      }}
      header={<PageHeader title='Intro to Rapid Ultrasonic Gridding (RUG)' />}
      footer={
        <PageFooter
          jobNumber={formData.mainInfo.jobNumber}
          revisionNumber={formData.revisionMetadata.revisionNumber}
        />
      }>
      <View style={{ marginBottom: 22 }}>
        <IntroText>
          A RUG inspection is a robot-enabled ultrasonic inspection that
          produces thickness grid maps to identify areas where corrosion and
          other damage mechanisms have caused wall-thinning. RUG is a
          replacement for traditional hand-held gridding with variable grid
          sizes from 12&quot; x 12&quot; to 1&quot; x 1&quot;.
        </IntroText>
        <IntroText>
          Typically, data collection occurs on a 1&quot; x 1&quot; grid
          (referred to as inspection resolution) and is then presented on a
          3&quot; x 3&quot; grid (known as deliverable resolution).
        </IntroText>
      </View>

      <View
        style={{
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: 22,
        }}>
        <Image
          src={robotImage}
          style={{ width: '100%', height: 210, objectFit: 'contain' }}
        />
      </View>

      <View style={{ marginBottom: 22 }}>
        <IntroText>
          The procedure that transitions the inspection resolution of 1&quot; x
          1&quot; to the deliverable resolution of 3&quot; x 3&quot; is commonly
          known as binning. The act of binning involves identifying the lowest
          thickness measurement within a specific 3&quot; x 3&quot; area, which
          consists of nine data points.
        </IntroText>
      </View>

      <View
        style={{
          justifyContent: 'center',
          marginLeft: 50,
          marginRight: 50,
          marginBottom: 22,
          alignItems: 'center',
        }}>
        <Image
          src={dataImage}
          style={{ width: '100%', height: 180, objectFit: 'contain' }}
        />
      </View>

      <View>
        <IntroText>
          The image above illustrates the final binned data output in Gecko
          Robotics software, on the left side, and a view of the original
          unbinned data within each data point on the right. This unbinned view
          is an internal function for data validation and localization.
        </IntroText>
      </View>
    </ReportPage>
  )
}

export const IntroToRUGSection: StaticSection = {
  id: RugReportSectionId.INTRO_TO_RUG,
  title: 'Intro to RUG',
  RenderPDF,
}
